// src/utils/kioskLock.js
//
// Blocco a schermo intero per la modalità kiosk.
//
// PERCHÉ ESISTE QUESTO MODULO
// Sui tablet Android capitava che l'operatore uscisse per sbaglio dalla schermata
// di timbratura toccando la barra di navigazione o tirando giù la status bar.
// Il codice precedente chiedeva il fullscreen una sola volta, subito dopo la
// configurazione del dispositivo: dopo un riavvio o un semplice reload il kiosk
// restava per sempre con le barre di sistema visibili.
//
// Qui centralizziamo tutte le difese e, soprattutto, le rendiamo AUTO-RIPARANTI:
// ogni volta che il blocco cade lo segnaliamo a chi ci ascolta, che copre l'app
// con un overlay finché un tocco non ripristina il fullscreen.
//
// LIMITE INVALICABILE, da tenere a mente prima di aggiungere altro codice qui:
// le barre di sistema Android NON sono bloccabili da una pagina web. Il
// fullscreen le nasconde, ma Android per policy le fa ricomparire con uno swipe
// dal bordo e i tasti Home/Recenti escono comunque dall'app. Il blocco davvero
// invalicabile è il "Blocco su schermo" di Android (vedi docs/BLOCCO_KIOSK_ANDROID.md).
// Questo modulo copre tutto il resto.

// Quanto spesso rivalutiamo lo stato del blocco anche senza eventi. Serve da rete
// di sicurezza: su alcune WebView Android il fullscreen cade senza emettere
// `fullscreenchange`.
const LOCK_POLL_INTERVAL_MS = 2000;

const state = {
  active: false,
  cleanups: [],
  wakeLock: null,
  wakeLockRetryTimer: null,
  pollTimer: null,
  onLockChange: null,
  allowUnload: false,
  allowUnloadTimer: null,
  fullscreenUnavailable: false
};

/** Registra un listener e memorizza come rimuoverlo, così `deactivate` pulisce tutto. */
const addListener = (target, event, handler, options) => {
  target.addEventListener(event, handler, options);
  state.cleanups.push(() => target.removeEventListener(event, handler, options));
};

/** True se il documento è in fullscreen tramite Fullscreen API (con prefissi vendor). */
export const isFullscreenActive = () =>
  Boolean(
    document.fullscreenElement ||
    document.webkitFullscreenElement ||
    document.mozFullScreenElement ||
    document.msFullscreenElement
  );

/**
 * True se l'app gira come PWA installata in modalità `fullscreen`.
 * In questo caso Android nasconde già status bar e barra di navigazione e la
 * Fullscreen API non c'entra nulla: `document.fullscreenElement` resta null.
 * Senza questo controllo l'overlay di blocco resterebbe visibile per sempre.
 */
export const isInstalledFullscreenPWA = () => {
  if (typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(display-mode: fullscreen)').matches;
};

/** True se l'app gira come PWA installata (fullscreen o standalone). */
export const isInstalledPWA = () => {
  if (isInstalledFullscreenPWA()) return true;
  if (typeof window.matchMedia === 'function' && window.matchMedia('(display-mode: standalone)').matches) {
    return true;
  }
  // iOS Safari non supporta display-mode
  return window.navigator.standalone === true;
};

/**
 * Il kiosk è "bloccato" se le barre di sistema sono nascoste, cioè se siamo in
 * fullscreen via API oppure se siamo una PWA installata in modalità fullscreen.
 */
export const isKioskLocked = () => isFullscreenActive() || isInstalledFullscreenPWA();

/**
 * Il fullscreen è irraggiungibile su questo dispositivo/browser?
 * Se sì smettiamo di insistere: meglio un kiosk con le barre visibili che un
 * kiosk coperto da un overlay che non si chiude e impedisce di timbrare.
 */
export const isFullscreenUnavailable = () => state.fullscreenUnavailable;

/**
 * Chiede il fullscreen. DEVE essere chiamata da un gesto utente: i browser
 * rifiutano la richiesta se arriva da un timer o dal mount di un componente.
 * @returns {Promise<boolean>} true se il kiosk risulta bloccato dopo il tentativo
 */
export const requestKioskFullscreen = async () => {
  // Già bloccato (tipicamente PWA installata in fullscreen): niente da fare.
  if (isKioskLocked()) return true;

  const el = document.documentElement;
  const request =
    el.requestFullscreen ||
    el.webkitRequestFullscreen ||
    el.mozRequestFullScreen ||
    el.msRequestFullscreen;

  if (!request) {
    state.fullscreenUnavailable = true;
    console.warn('[KIOSK-LOCK] Fullscreen API non supportata su questo browser');
    return false;
  }

  try {
    await request.call(el, { navigationUI: 'hide' });
    state.fullscreenUnavailable = false;
    return true;
  } catch (error) {
    // Alcuni browser rifiutano l'oggetto opzioni: riproviamo senza.
    try {
      await request.call(el);
      state.fullscreenUnavailable = false;
      return true;
    } catch (retryError) {
      console.warn('[KIOSK-LOCK] Richiesta fullscreen rifiutata:', retryError?.message || retryError);
      state.fullscreenUnavailable = true;
      return false;
    }
  }
};

/** Esce dal fullscreen (usata dai flussi di uscita d'emergenza). */
export const exitKioskFullscreen = () => {
  const exit =
    document.exitFullscreen ||
    document.webkitExitFullscreen ||
    document.mozCancelFullScreen ||
    document.msExitFullscreen;

  if (exit && isFullscreenActive()) {
    try {
      exit.call(document);
    } catch (error) {
      console.warn('[KIOSK-LOCK] Uscita fullscreen fallita:', error?.message || error);
    }
  }
};

/**
 * Consente il PROSSIMO abbandono della pagina senza il dialogo di conferma.
 * Va chiamata prima di ogni reload voluto (auto-aggiornamento kiosk, logout
 * d'emergenza), altrimenti la guardia `beforeunload` blocca i deploy sui tablet.
 */
export const allowNextUnload = () => {
  state.allowUnload = true;
  // Se il reload non avviene (annullato, offline, errore) la guardia si riarma
  // da sola: un kiosk non deve restare scoperto per una navigazione mancata.
  if (state.allowUnloadTimer) clearTimeout(state.allowUnloadTimer);
  state.allowUnloadTimer = setTimeout(() => {
    state.allowUnloadTimer = null;
    if (state.active) state.allowUnload = false;
  }, 10000);
};

// --- Wake lock -------------------------------------------------------------
// Android rilascia il wake lock ogni volta che la pagina passa in background.
// Il codice precedente lo chiedeva una volta sola: dopo il primo spegnimento
// dello schermo il tablet tornava a spegnersi da solo. Qui lo riacquisiamo.

const releaseWakeLock = async () => {
  if (state.wakeLockRetryTimer) {
    clearTimeout(state.wakeLockRetryTimer);
    state.wakeLockRetryTimer = null;
  }
  if (state.wakeLock) {
    try {
      await state.wakeLock.release();
    } catch (error) {
      /* già rilasciato */
    }
    state.wakeLock = null;
  }
};

const acquireWakeLock = async () => {
  if (!('wakeLock' in navigator) || !state.active) return;
  if (state.wakeLock && !state.wakeLock.released) return;
  if (document.visibilityState !== 'visible') return;

  try {
    state.wakeLock = await navigator.wakeLock.request('screen');
    state.wakeLock.addEventListener('release', () => {
      state.wakeLock = null;
      // Ritenta più tardi: se siamo in background fallirebbe comunque.
      if (state.active && !state.wakeLockRetryTimer) {
        state.wakeLockRetryTimer = setTimeout(() => {
          state.wakeLockRetryTimer = null;
          acquireWakeLock();
        }, 5000);
      }
    });
  } catch (error) {
    console.warn('[KIOSK-LOCK] Wake lock non disponibile:', error?.message || error);
  }
};

// --- Attivazione -----------------------------------------------------------

/**
 * Attiva tutte le difese kiosk.
 * @param {object} options
 * @param {(locked: boolean) => void} options.onLockChange chiamata a ogni variazione
 *        dello stato di blocco (e una volta subito, con lo stato iniziale)
 * @returns {() => void} funzione di disattivazione
 */
export const activateKioskLock = ({ onLockChange } = {}) => {
  if (state.active) {
    // Riattivazione: aggiorniamo solo la callback (StrictMode monta due volte in dev).
    state.onLockChange = onLockChange || null;
    if (state.onLockChange) state.onLockChange(isKioskLocked());
    return deactivateKioskLock;
  }

  state.active = true;
  state.onLockChange = onLockChange || null;
  state.allowUnload = false;

  const notifyLockChange = () => {
    if (state.onLockChange) state.onLockChange(isKioskLocked());
  };

  // 1. Fullscreen: reagisci quando cade (swipe sul bordo, notifica di sistema,
  //    schermo spento e riacceso...) così l'overlay può richiederlo di nuovo.
  ['fullscreenchange', 'webkitfullscreenchange', 'mozfullscreenchange', 'MSFullscreenChange'].forEach(evt => {
    addListener(document, evt, notifyLockChange);
  });

  // 2. Cambio di display-mode (la PWA passa da fullscreen a browser e viceversa).
  if (typeof window.matchMedia === 'function') {
    const mq = window.matchMedia('(display-mode: fullscreen)');
    if (typeof mq.addEventListener === 'function') {
      mq.addEventListener('change', notifyLockChange);
      state.cleanups.push(() => mq.removeEventListener('change', notifyLockChange));
    }
  }

  // 3. Ritorno in primo piano: riacquisisci il wake lock e rivaluta il blocco.
  addListener(document, 'visibilitychange', () => {
    if (document.visibilityState === 'visible') {
      acquireWakeLock();
    }
    notifyLockChange();
  });
  addListener(window, 'focus', notifyLockChange);
  addListener(window, 'blur', notifyLockChange);

  // 4. Tasto/gesto INDIETRO di Android. In una PWA installata con cronologia
  //    vuota il "back" chiude l'applicazione: piantiamo uno stato sentinella e
  //    lo ripiantiamo a ogni popstate, così indietro non porta da nessuna parte.
  try {
    window.history.pushState({ kioskLock: true }, document.title);
  } catch (error) {
    console.warn('[KIOSK-LOCK] pushState non riuscito:', error?.message || error);
  }
  addListener(window, 'popstate', () => {
    if (!state.active) return;
    try {
      window.history.pushState({ kioskLock: true }, document.title);
    } catch (error) {
      /* ignora */
    }
  });

  // 5. Guardia sulla chiusura/ricarica accidentale della pagina.
  addListener(window, 'beforeunload', (e) => {
    if (state.allowUnload) return undefined;
    e.preventDefault();
    e.returnValue = '';
    return '';
  });

  // 6. Menu contestuale, selezione testo e drag: il long-press su Android apre
  //    il menu di Chrome ("apri in un'altra scheda", "cerca"...), da cui si esce
  //    dal kiosk. La guida di setup lo dava per fatto, ma non lo era.
  ['contextmenu', 'selectstart', 'dragstart'].forEach(evt => {
    addListener(document, evt, (e) => e.preventDefault(), { passive: false });
  });

  // 7. Zoom con pinch e doppio tap (il meta viewport copre il resto).
  addListener(document, 'gesturestart', (e) => e.preventDefault(), { passive: false });
  addListener(document, 'touchstart', (e) => {
    if (e.touches && e.touches.length > 1) e.preventDefault();
  }, { passive: false });

  // 8. Scroll e pull-to-refresh: comportamento già presente prima del refactor,
  //    mantenuto qui in modo che venga anche rimosso correttamente.
  const preventDefault = (e) => e.preventDefault();
  addListener(document, 'touchmove', preventDefault, { passive: false });
  addListener(document, 'wheel', preventDefault, { passive: false });

  // 9. Scorciatoie da tastiera (tablet con tastiera collegata o chiavetta OTG).
  //    Escape resta libero: serve al flusso di uscita d'emergenza.
  addListener(document, 'keydown', (e) => {
    if (e.key === 'Escape') return;
    const blocked =
      e.key === 'F12' ||
      e.key === 'F5' ||
      (e.ctrlKey && e.shiftKey && (e.key === 'I' || e.key === 'J' || e.key === 'C')) ||
      (e.ctrlKey && (e.key === 'u' || e.key === 'r' || e.key === 'w' || e.key === 'n' || e.key === 't'));
    if (blocked) e.preventDefault();
  });

  // 10. Blocco dell'orientamento: best effort, funziona solo in fullscreen.
  if (window.screen?.orientation?.lock) {
    window.screen.orientation.lock('any').catch(() => { /* non supportato: ignora */ });
  }

  // 11. Wake lock con riacquisizione.
  acquireWakeLock();

  // 12. Rete di sicurezza: alcune WebView non emettono `fullscreenchange`.
  let lastKnownLock = isKioskLocked();
  state.pollTimer = setInterval(() => {
    const current = isKioskLocked();
    if (current !== lastKnownLock) {
      lastKnownLock = current;
      notifyLockChange();
    }
  }, LOCK_POLL_INTERVAL_MS);

  document.body.classList.add('kiosk-locked-body');

  notifyLockChange();

  return deactivateKioskLock;
};

/** Rimuove ogni difesa e ripristina il comportamento normale del browser. */
export const deactivateKioskLock = () => {
  if (!state.active) return;

  state.active = false;
  state.allowUnload = true;

  state.cleanups.forEach(fn => {
    try {
      fn();
    } catch (error) {
      /* ignora */
    }
  });
  state.cleanups = [];

  if (state.pollTimer) {
    clearInterval(state.pollTimer);
    state.pollTimer = null;
  }

  if (state.allowUnloadTimer) {
    clearTimeout(state.allowUnloadTimer);
    state.allowUnloadTimer = null;
  }

  releaseWakeLock();
  state.onLockChange = null;
  document.body.classList.remove('kiosk-locked-body');
};

/**
 * Sostituisce il manifest della pagina con quello dedicato al kiosk.
 *
 * Il manifest principale usa `display: "standalone"`, che su Android lascia
 * visibili status bar e barra di navigazione. Il manifest kiosk usa
 * `display: "fullscreen"`, che le nasconde entrambe senza bisogno di app di
 * terze parti. Chrome legge il manifest del documento corrente al momento
 * dell'installazione, quindi lo sostituiamo il prima possibile all'avvio.
 *
 * ATTENZIONE: una PWA GIÀ installata non cambia modalità di visualizzazione da
 * sola. Va disinstallata e reinstallata. Vedi docs/BLOCCO_KIOSK_ANDROID.md.
 */
export const applyKioskManifest = () => {
  try {
    const link = document.querySelector('link[rel="manifest"]');
    if (!link) return;
    if (link.getAttribute('href')?.includes('manifest-kiosk.json')) return;
    link.setAttribute('href', '/manifest-kiosk.json');
  } catch (error) {
    console.warn('[KIOSK-LOCK] Sostituzione manifest non riuscita:', error?.message || error);
  }
};

const kioskLock = {
  activateKioskLock,
  deactivateKioskLock,
  requestKioskFullscreen,
  exitKioskFullscreen,
  isKioskLocked,
  isFullscreenActive,
  isFullscreenUnavailable,
  isInstalledPWA,
  isInstalledFullscreenPWA,
  allowNextUnload,
  applyKioskManifest
};

export default kioskLock;
